/* Everything done *to* this definition, from the tamper-evident audit chain.
 *
 * **Runs are deliberately absent.** A run is its own aggregate with its own id, so it does not
 * appear in a query by this definition's id — and that is right rather than a limitation: the runs
 * ledger above is where runs live, and merging the two would be a timeline this platform never
 * recorded. What is here is authoring, tolerance publications, policy changes and every scheduled
 * occurrence, which all key on the definition.
 *
 * **Gated before it is fetched.** Reading the chain needs `audit.chain:read`, which `data-steward`
 * and `reader` hold. Whoever does not gets a quiet, honest absence rather than a request that 403s —
 * an error here would read as a fault, and nothing about running or configuring this definition
 * needs the chain.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { can, READ_AUDIT_CHAIN_PERMISSION, useIdentity } from '@/entities/session';
import { Alert } from '@/shared/ui/Alert';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useDefinitionHistory } from '../api/governance';
import { historyRows, isSystemActor } from '../model/governance';

export function DefinitionHistory({
  definitionId,
}: {
  readonly definitionId: string;
}): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const readable = can(identity.data, READ_AUDIT_CHAIN_PERMISSION);
  const history = useDefinitionHistory(definitionId, { enabled: readable });

  return (
    <>
      <SectionHead title={t('reconciliation.history.title')} />
      <section className="lq-card lq-rc__panel">
        {!readable && (
          <>
            <span className="lq-rc__badge lq-rc__badge--muted">
              {t('reconciliation.history.restricted')}
            </span>
            <p className="lq-note">{t('reconciliation.history.restricted.body')}</p>
          </>
        )}

        {readable && history.isPending && <SkeletonRows rows={4} columns={4} />}

        {readable && history.isError && (
          <Alert tone="error" title={t('reconciliation.history.failed')}>
            {history.error.message}
          </Alert>
        )}

        {readable && history.isSuccess && history.data.length === 0 && (
          <p className="lq-note">{t('reconciliation.history.empty')}</p>
        )}

        {readable && history.isSuccess && history.data.length > 0 && (
          <>
            {historyRows(history.data).map((row) => (
              <div key={row.key} className="lq-rc__histRow">
                <div className="lq-rc__histWhen">
                  {new Date(row.occurredAt).toLocaleString()}
                </div>
                <div className={`lq-rc__histVerb lq-rc__histVerb--${row.tone}`}>
                  {row.action}
                </div>
                <div className="lq-rc__histWhat">
                  {t(`reconciliation.history.action.${row.action}`, {
                    defaultValue: row.action,
                  })}
                </div>
                <div className="lq-rc__histWho">
                  {isSystemActor(row.actor) ? (
                    /* A timer, not a person. Rendering `system:reconciliation-scheduler` as a name
                       would put a string nobody can be asked about in the accountability column. */
                    <span className="lq-muted">
                      {t('reconciliation.history.scheduler')}
                    </span>
                  ) : (
                    row.actor
                  )}
                </div>
              </div>
            ))}
            <p className="lq-note">{t('reconciliation.history.source')}</p>
          </>
        )}
      </section>
    </>
  );
}
